import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getDictionary } from "generated/api/dictionary";
import type {
  GetCampaignClassesCampaignObjective,
  GetCampaignObjectivesProgramType,
  GetTouchpointContentTypesCampaignObjective,
  TouchpointDTOContentType,
} from "generated/schemas";

import type { OptionType } from "ui/select";

import {
  AUDIENCE_TYPES,
  type NamedDictionaryItem,
  type TouchpointStatusDictionaryItem,
} from "./dictionary.types";
import { mapDictionaryItemsToMultiSelectOptions } from "./dictionary.utils";

const dictionaryClient = getDictionary();

export interface ContentTypeDictionaryItem extends Omit<NamedDictionaryItem, "name"> {
  name: TouchpointDTOContentType;
}

/**
 * Dictionary endpoints serve effectively static reference data, so queries are
 * marked non-stale to mirror the previous RTK Query caching (fetch once, reuse).
 */
const STALE_TIME = Infinity;

const dictionaryKeys = {
  all: ["dictionary"] as const,
  audienceTypes: () => [...dictionaryKeys.all, "audience-types"] as const,
  populationTypes: () => [...dictionaryKeys.all, "population-types"] as const,
  campaignClassTypes: (campaignObjective?: string) =>
    [...dictionaryKeys.all, "class-types", campaignObjective ?? null] as const,
  campaignObjectiveTypes: (programType?: string) =>
    [...dictionaryKeys.all, "campaign-objective-types", programType ?? null] as const,
  programTypes: () => [...dictionaryKeys.all, "program-types"] as const,
  touchpointContentTypes: (campaignObjective?: string) =>
    [...dictionaryKeys.all, "touchpoint-content-types", campaignObjective ?? null] as const,
  touchpointVersionStatuses: () => [...dictionaryKeys.all, "touchpoint-version-statuses"] as const,
  batchStatuses: () => [...dictionaryKeys.all, "touchpoint-batch-statuses"] as const,
};

const useDictionaryOptions = <T extends NamedDictionaryItem>(data: T[] | undefined): OptionType[] =>
  useMemo(() => mapDictionaryItemsToMultiSelectOptions(data), [data]);

/**
 * @example `[{ "name": "FRESHMAN", "description": "Freshman" }]` */
export const useGetCampaignClassTypesQuery = (campaignObjective?: string, enabled = true) =>
  useQuery({
    queryKey: dictionaryKeys.campaignClassTypes(campaignObjective),
    queryFn: () =>
      dictionaryClient.getCampaignClasses(
        campaignObjective
          ? { campaignObjective: campaignObjective as GetCampaignClassesCampaignObjective }
          : undefined,
      ),
    staleTime: STALE_TIME,
    enabled,
  });

export const useClassTypeOptions = (campaignObjective?: string, enabled = true) => {
  const query = useGetCampaignClassTypesQuery(campaignObjective, enabled);
  const classTypeOptions = useDictionaryOptions(query.data);

  return {
    ...query,
    classTypes: query.data ?? [],
    classTypeOptions,
  };
};

/**
 * @example `[{ "name": "APPLICATION_GENERATION", "description": "Application Generation" }]` */
export const useGetCampaignObjectiveTypesQuery = (programType?: string, enabled = true) =>
  useQuery({
    queryKey: dictionaryKeys.campaignObjectiveTypes(programType),
    queryFn: () =>
      dictionaryClient.getCampaignObjectives(
        programType ? { programType: programType as GetCampaignObjectivesProgramType } : undefined,
      ),
    staleTime: STALE_TIME,
    enabled,
  });

export const useCampaignObjectiveTypeOptions = (programType?: string, enabled = true) => {
  const query = useGetCampaignObjectiveTypesQuery(programType, enabled);
  const campaignObjectiveTypeOptions = useDictionaryOptions(query.data);

  return {
    ...query,
    campaignObjectiveTypes: query.data ?? [],
    campaignObjectiveTypeOptions,
  };
};

/**
 * @example `[{ "name": "FAFSA", "description": "FAFSA" }]` */
export const useGetTouchpointContentTypesQuery = (campaignObjective?: string, enabled = true) =>
  useQuery({
    queryKey: dictionaryKeys.touchpointContentTypes(campaignObjective),
    queryFn: () =>
      dictionaryClient.getTouchpointContentTypes(
        campaignObjective
          ? { campaignObjective: campaignObjective as GetTouchpointContentTypesCampaignObjective }
          : undefined,
      ) as Promise<ContentTypeDictionaryItem[]>,
    staleTime: STALE_TIME,
    enabled,
  });

export const useTouchpointContentTypeOptions = (campaignObjective?: string, enabled = true) => {
  const query = useGetTouchpointContentTypesQuery(campaignObjective, enabled);
  const contentTypeOptions = useDictionaryOptions(query.data);

  return {
    ...query,
    contentTypes: query.data ?? [],
    contentTypeOptions,
  };
};

/**
 * Served from a local constant pending a backend endpoint.
 * @example `[{ "name": "TRANSFER_PROSPECTS", "description": "Transfer Prospects" }]` */
export const useGetAudienceTypesQuery = () =>
  useQuery({
    queryKey: dictionaryKeys.audienceTypes(),
    queryFn: async (): Promise<NamedDictionaryItem[]> => AUDIENCE_TYPES,
    staleTime: STALE_TIME,
  });

/**
 * @example `[{ "name": "PARENT", "description": "Parent" }, { "name": "STUDENT", "description": "Student" }]` */
export const useGetPopulationTypesQuery = () =>
  useQuery({
    queryKey: dictionaryKeys.populationTypes(),
    queryFn: () => dictionaryClient.getPopulationTypes(),
    staleTime: STALE_TIME,
  });

export const usePopulationTypeOptions = () => {
  const query = useGetPopulationTypesQuery();
  const populationTypeOptions = useDictionaryOptions(query.data);

  return {
    ...query,
    populationTypes: query.data ?? [],
    populationTypeOptions,
  };
};

/**
 * @returns `[{ "name": "AWARE_TO_APPLY", "description": "Aware to Apply" }, { "name": "ADMIT_TO_ENROLL", "description": "Admit to Enroll" }]` */
export const useGetProgramTypesQuery = () =>
  useQuery({
    queryKey: dictionaryKeys.programTypes(),
    queryFn: () => dictionaryClient.getProgramTypes(),
    staleTime: STALE_TIME,
  });

/**
 * @example `[{ "status": "DRAFT", "clientReviewStatusName": "Draft", "versionStatusName": "Draft" }]` */
export const useGetTouchpointVersionStatusesQuery = () =>
  useQuery({
    queryKey: dictionaryKeys.touchpointVersionStatuses(),
    queryFn: () =>
      dictionaryClient.getVersionStatuses() as Promise<TouchpointStatusDictionaryItem[]>,
    staleTime: STALE_TIME,
  });

/**
 * @example `[{ "status": "DRAFT", "description": "Not Reviewed Yest" }]` */
export const useGetBatchStatusesQuery = () =>
  useQuery({
    queryKey: dictionaryKeys.batchStatuses(),
    queryFn: () => dictionaryClient.getTouchpointBatchStatuses(),
    staleTime: STALE_TIME,
  });
